import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BurmaRubyBg from '../../assets/image/background/burma ruby.jpeg';
import MozambiqueRubyBg from '../../assets/image/background/mozambique ruby.jpeg';
import StarRubyBg from '../../assets/image/background/start ruby.jpeg';
import StarRubyImg from '../../assets/image/background/start.jpeg';
import AquamarineImg from '../../assets/image/Aquamarine.jpeg';
import AlexandriteImg from '../../assets/image/Alexandrite.jpeg';
import BlueZirconImg from '../../assets/image/Blue Zircon.jpeg';
import ParaibaTourmalineImg from '../../assets/image/Paraiba Tourmaline.jpeg';
import BlueTourmalineImg from '../../assets/image/Tourmaline Collection.jpeg';
import DemantoidGarnetImg from '../../assets/image/Demantoid Garnet.jpg';
import MaliGarnetImg from '../../assets/image/Mali Garne.jpeg';
import SpinelImg from '../../assets/image/Spinel.jpeg';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 60vh;
`;

const HeroSection = styled.section`
  height: 50vh;
  min-height: 400px;
  background-image: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.4)), url(${props => props.image});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-bottom: 60px;
  border-radius: 20px;
  position: relative;
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  margin-bottom: 20px;
  letter-spacing: 3px;
  font-weight: 300;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  span {
    color: #c4a484;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  margin-top: 40px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
`;

const MainImage = styled.img`
  width: 100%;
  height: 500px;
  object-fit: contain;
  object-position: center;
  background-color: #f8f9fa;
`;

const InfoSection = styled.div`
  padding: 20px;
`;

const GemName = styled.h2`
  font-size: 48px;
  color: #1a2a3a;
  margin-bottom: 20px;
  font-weight: 300;
  
  span {
    color: #c4a484;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #444;
  margin-bottom: 30px;
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px 0;
`;

const PropertyCard = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  
  h4 {
    color: #c4a484;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 10px;
  }
  
  p {
    color: #1a2a3a;
    font-size: 18px;
    font-weight: 500;
  }
`;

const PriceInfo = styled.div`
  background: linear-gradient(135deg, #1a2a3a, #2c3e50);
  color: white;
  padding: 25px;
  border-radius: 10px;
  margin-top: 30px;
  
  h3 {
    color: #c4a484;
    font-size: 18px;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  p {
    font-size: 24px;
    font-weight: 300;
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: 2px solid #c4a484;
  color: #c4a484;
  padding: 10px 30px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 30px;
  cursor: pointer;
  margin-bottom: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #c4a484;
    color: white;
  }
`;

const NotFoundSection = styled.div`
  text-align: center;
  padding: 100px 20px;
  
  h2 {
    font-size: 48px;
    color: #1a2a3a;
    margin-bottom: 20px;
  }
  
  p {
    font-size: 18px;
    color: #666;
    margin-bottom: 30px;
  }
`;

// Hero background images for gems
const heroBackgrounds = {
  'Burma Ruby': 'https://images.unsplash.com/photo-1599643477877-530eb83abc8e?w=1920&q=90',
  'Mozambique Ruby': 'https://images.unsplash.com/photo-1615655114865-4cc1bda5901e?w=1920&q=80',
  'Star Ruby': StarRubyBg,
  'Paraiba Tourmaline': BlueZirconImg
};

// Fallback images for gems
const fallbackImages = {
  'Burma Ruby': BurmaRubyBg,
  'Mozambique Ruby': MozambiqueRubyBg,
  'Star Ruby': StarRubyBg,
  'Aquamarine': AquamarineImg,
  'Alexandrite': AlexandriteImg,
  'Blue Zircon': BlueZirconImg,
  'Paraiba Tourmaline': ParaibaTourmalineImg,
  'Blue Tourmaline': BlueTourmalineImg,
  'Demantoid Garnet': DemantoidGarnetImg,
  'Grossular Garnet': MaliGarnetImg,
  'Mali Garnet': MaliGarnetImg,
  'Spinel': SpinelImg,
  'default': 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
};

// Gem properties database
const gemProperties = {
  'Burma Ruby': {
    hardness: '9 (Mohs scale)',
    color: 'Pigeon Blood Red',
    origin: 'Myanmar (Burma)',
    clarity: 'Eye-Clean',
    rarity: 'Very Rare',
    price: '$15,000 - $100,000+ per carat',
    description: 'The finest rubies in the world, known for their intense "pigeon blood" red color and exceptional fluorescence. Burmese rubies are prized for their deep red hue with a hint of blue, created by the unique geological conditions in Myanmar. These gems have adorned royal crowns and are considered the ultimate symbol of passion and power.'
  },
  'Mozambique Ruby': {
    hardness: '9 (Mohs scale)',
    color: 'Vivid Red',
    origin: 'Mozambique',
    clarity: 'Excellent',
    rarity: 'Fine',
    price: '$5,000 - $30,000 per carat',
    description: 'Vibrant red rubies from Mozambique that rival Burmese quality. These rubies are known for their excellent clarity and bright red fluorescence, making them highly sought after in the modern gem market. They offer exceptional value and beauty, with colors ranging from pinkish-red to deep crimson.'
  },
  'Star Ruby': {
    hardness: '9 (Mohs scale)',
    color: 'Red with Star Effect',
    origin: 'Sri Lanka',
    clarity: 'Translucent',
    rarity: 'Rare',
    price: '$3,000 - $50,000 per carat',
    description: 'Rubies that display a rare asterism effect - a six-rayed star that moves across the surface when rotated. This phenomenon is caused by tiny rutile inclusions that reflect light in a star pattern. Each star ruby is unique, with the star\'s sharpness, centered position, and visibility determining its value.'
  },
  'Aquamarine': {
    hardness: '7.5-8 (Mohs scale)',
    color: 'Blue-Green',
    origin: 'Brazil, Madagascar, Nigeria',
    clarity: 'Eye-Clean',
    rarity: 'Fine',
    price: '$500 - $5,000 per carat',
    description: 'Aquamarine is a breathtaking blue-green gemstone that captures the essence of the ocean. Its name derives from the Latin "aqua marina" meaning "sea water." These gems are known for their exceptional clarity and calming color that ranges from pale sky blue to deep teal.'
  },
  'Alexandrite': {
    hardness: '8.5 (Mohs scale)',
    color: 'Green to Red (Color Change)',
    origin: 'Russia, Brazil, Sri Lanka',
    clarity: 'Eye-Clean',
    rarity: 'Extremely Rare',
    price: '$15,000 - $100,000+ per carat',
    description: 'Alexandrite is one of the rarest and most valuable gemstones in the world, famous for its remarkable color-changing ability. It appears green in daylight and transforms to a rich red or purplish-red under incandescent light. This extraordinary phenomenon is caused by the gemstone\'s unique ability to absorb light differently in various wavelengths.'
  },
  'Blue Zircon': {
    hardness: '6-7.5 (Mohs scale)',
    color: 'Blue',
    origin: 'Cambodia, Sri Lanka, Vietnam',
    clarity: 'Eye-Clean',
    rarity: 'Fine',
    price: '$200 - $2,000 per carat',
    description: 'Blue zircon is a dazzling gemstone known for its brilliant sparkle and vivid blue color. Despite its name, zircon is a natural gemstone and not related to cubic zirconia. It possesses exceptional brilliance and fire, often displaying rainbow-like dispersions of light.'
  },
  'Paraiba Tourmaline': {
    hardness: '7-7.5 (Mohs scale)',
    color: 'Neon Blue-Green',
    origin: 'Brazil, Mozambique, Nigeria',
    clarity: 'Eye-Clean to Included',
    rarity: 'Extremely Rare',
    price: '$10,000 - $100,000+ per carat',
    description: 'Paraiba tourmaline is one of the rarest and most valuable gemstones in the world, famous for its vivid neon blue-green color caused by copper and manganese content. Discovered in the 1980s in Brazil\'s Paraiba state, these gems display an unmatched electric glow that seems to come from within. The finest specimens command astronomical prices and are highly sought after by collectors worldwide.'
  },
  'Blue Tourmaline': {
    hardness: '7-7.5 (Mohs scale)',
    color: 'Blue to Blue-Green',
    origin: 'Brazil, Afghanistan, Namibia',
    clarity: 'Eye-Clean',
    rarity: 'Rare',
    price: '$500 - $5,000 per carat',
    description: 'Blue tourmaline (indicolite) is a stunning gemstone prized for its deep blue to blue-green coloration. These gems offer excellent clarity and a beautiful saturation that ranges from pale azure to deep sapphire blue. They have become increasingly popular in contemporary jewelry designs.'
  },
  'Demantoid Garnet': {
    hardness: '6.5-7 (Mohs scale)',
    color: 'Emerald Green',
    origin: 'Russia, Iran, Namibia',
    clarity: 'Eye-Clean to Included',
    rarity: 'Extremely Rare',
    price: '$5,000 - $50,000+ per carat',
    description: 'Demantoid garnet is the rarest and most valuable member of the garnet family, renowned for its brilliant emerald green color and exceptional fire (dispersion). Often called the "emerald of the Urals," these gems were once favored by Russian Tsars and Cartier. Their remarkable brilliance exceeds even diamond, making them truly captivating.'
  },
  'Grossular Garnet': {
    hardness: '6.5-7.5 (Mohs scale)',
    color: 'Green, Yellow, Orange',
    origin: 'Kenya, Tanzania, Canada',
    clarity: 'Eye-Clean',
    rarity: 'Fine',
    price: '$500 - $5,000 per carat',
    description: 'Grossular garnet is a vibrant member of the garnet family, displaying colors ranging from verdant green to sunny yellow and fiery orange. The rare green variety (tsavorite) is one of the most valuable garnets. These gems are known for their excellent clarity and brilliance.'
  },
  'Mali Garnet': {
    hardness: '7-7.5 (Mohs scale)',
    color: 'Green to Yellow-Green',
    origin: 'Mali',
    clarity: 'Eye-Clean',
    rarity: 'Rare',
    price: '$300 - $2,000 per carat',
    description: 'Mali garnet is a rare and recent discovery from West Africa, specifically Mali. This unique garnet displays a beautiful dispersion (fire) that creates rainbow flashes when light enters the stone. It combines the best properties of grossular and andradite garnets.'
  },
  'Spinel': {
    hardness: '7.5-8 (Mohs scale)',
    color: 'Various (Red, Blue, Pink)',
    origin: 'Myanmar, Sri Lanka, Vietnam',
    clarity: 'Eye-Clean',
    rarity: 'Rare to Fine',
    price: '$1,000 - $30,000+ per carat',
    description: 'Spinel is a magnificent gemstone that has been mistaken for ruby and sapphire throughout history. Available in a stunning array of colors including vivid red, cobalt blue, hot pink, and violet, spinel is prized for its exceptional clarity, brilliance, and durability. The finest specimens rival the most expensive rubies.'
  }
};

const GemstoneDetail = () => {
  const { gemName } = useParams();
  const navigate = useNavigate();
  
  // Format the gem name from URL (e.g., "burma-ruby" -> "Burma Ruby")
  const formattedName = gemName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  console.log('GemstoneDetail rendered for:', formattedName);
  
  // Get gem properties
  const gemData = gemProperties[formattedName];
  
  // If gem not found
  if (!gemData) {
    return (
      <PageContainer>
        <NotFoundSection>
          <h2>Gemstone Not Found</h2>
          <p>Sorry, we couldn't find information about "{formattedName}"</p>
          <BackButton onClick={() => navigate('/')}>Return to Home</BackButton>
        </NotFoundSection>
      </PageContainer>
    );
  }

  const heroBackground = heroBackgrounds[formattedName] || fallbackImages.default;
  const mainImage = formattedName === 'Burma Ruby' 
    ? BurmaRubyBg 
    : (formattedName === 'Mozambique Ruby' 
        ? MozambiqueRubyBg 
        : (formattedName === 'Star Ruby' 
            ? StarRubyImg 
            : (fallbackImages[formattedName] || fallbackImages.default)));

  return (
    <PageContainer>
      <BackButton onClick={() => navigate(-1)}>← Back to Gems</BackButton>
      
      <HeroSection image={heroBackground}>
        <HeroTitle>
          <span>{formattedName.split(' ')[0]}</span> {formattedName.split(' ').slice(1).join(' ')}
        </HeroTitle>
      </HeroSection>

      <ContentSection>
        <ImageWrapper>
          <MainImage src={mainImage} alt={formattedName} />
        </ImageWrapper>
        
        <InfoSection>
          <GemName>
            The <span>{formattedName}</span>
          </GemName>
          
          <Description>{gemData.description}</Description>
          
          <PropertiesGrid>
            <PropertyCard>
              <h4>Hardness</h4>
              <p>{gemData.hardness}</p>
            </PropertyCard>
            <PropertyCard>
              <h4>Color</h4>
              <p>{gemData.color}</p>
            </PropertyCard>
            <PropertyCard>
              <h4>Origin</h4>
              <p>{gemData.origin}</p>
            </PropertyCard>
            <PropertyCard>
              <h4>Rarity</h4>
              <p>{gemData.rarity}</p>
            </PropertyCard>
          </PropertiesGrid>
          
          <PriceInfo>
            <h3>Estimated Value</h3>
            <p>{gemData.price}</p>
          </PriceInfo>
        </InfoSection>
      </ContentSection>
    </PageContainer>
  );
};

export default GemstoneDetail;